import { MutableRefObject } from 'react';

import { axios } from '../../api';
import { dashboardRequestQueue } from '../../utils/requestQueue';

// Check if endpoint has changed
export const hasEndpointChanged = (
  currentEndpoint: string,
  prevEndpoint: MutableRefObject<string>
) => {
  return prevEndpoint.current !== currentEndpoint;
};

// Reset data when endpoint changes
export const resetDataForNewEndpoint = (
  setData: (data: null) => void,
  loadedEndpoint: MutableRefObject<string | null>
) => {
  setData(null);
  loadedEndpoint.current = null;
};

// Check if this is the initial load
export const checkIsInitialLoad = (
  currentEndpoint: string,
  loadedEndpoint: MutableRefObject<string | null>
) => {
  return loadedEndpoint.current !== currentEndpoint;
};

// Check if global refresh was triggered
export const checkIsGlobalRefresh = (
  currentRefetchTrigger: number | undefined,
  currentEndpoint: string,
  prevRefetchTrigger: MutableRefObject<number | undefined>,
  prevEndpoint: MutableRefObject<string>
) => {
  return (
    currentRefetchTrigger !== undefined &&
    prevRefetchTrigger.current !== undefined &&
    currentRefetchTrigger !== prevRefetchTrigger.current &&
    prevEndpoint.current === currentEndpoint
  );
};

// Check if tile-specific refresh was triggered
export const checkIsTileRefresh = (
  currentTileRefetchTrigger: number,
  currentEndpoint: string,
  prevTileRefetchTrigger: MutableRefObject<number>,
  prevEndpoint: MutableRefObject<string>
) => {
  return (
    currentTileRefetchTrigger !== prevTileRefetchTrigger.current &&
    prevEndpoint.current === currentEndpoint
  );
};

// Build the API URL
export const buildApiUrl = (
  currentEndpoint: string,
  isGlobalRefresh: boolean,
  isTileRefresh: boolean
) => {
  const baseUrl = `/on_call_dashboard/${currentEndpoint}`;
  const isManualRefresh = isGlobalRefresh || isTileRefresh;
  return isManualRefresh ? `${baseUrl}?invalidate_cache=true` : baseUrl;
};

// Update all refs to current values
export const updateRefs = (
  currentRefetchTrigger: number | undefined,
  currentTileRefetchTrigger: number,
  currentEndpoint: string,
  prevRefetchTrigger: MutableRefObject<number | undefined>,
  prevTileRefetchTrigger: MutableRefObject<number>,
  prevEndpoint: MutableRefObject<string>
) => {
  prevRefetchTrigger.current = currentRefetchTrigger;
  prevTileRefetchTrigger.current = currentTileRefetchTrigger;
  prevEndpoint.current = currentEndpoint;
};

// Execute the API fetch
export const executeFetch = async (
  url: string,
  currentEndpoint: string,
  setIsLoading: (loading: boolean) => void,
  setError: (error: string | null) => void,
  setData: (data: any) => void,
  loadedEndpoint: MutableRefObject<string | null>
) => {
  setIsLoading(true);
  setError(null);

  try {
    const response = await dashboardRequestQueue.add(() => axios.get(url));
    setData(response.data);
    loadedEndpoint.current = currentEndpoint;
  } catch (e: any) {
    setError(e.message || 'Failed to load data');
  } finally {
    setIsLoading(false);
  }
};
